import React, { useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import moment from 'moment';
import 'moment/locale/fr';
import TransferService from "../services/TransferService";
import RoleBasedRender from "./RoleBasedRender";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faClock,
    faUser,
    faEnvelope,
    faPaperPlane,
    faBan,
    faExchangeAlt,
    faUniversity,
    faCreditCard,
    faMoneyBillAlt
} from '@fortawesome/free-solid-svg-icons';

const RejectedTransferList = ({ transfers, onTransferClick, onRefresh }) => {
    const [loadingButton, setLoadingButton] = useState(null);
    moment.locale('fr');

    const getMethodIcon = (method) => {
        switch(method.toLowerCase()) {
            case 'bank':
                return faUniversity;
            case 'card':
                return faCreditCard;
            case 'cash':
                return faMoneyBillAlt;
            default:
                return faExchangeAlt;
        }
    };

    const handleValidateTransfer = async (e, transfer) => {
        e.stopPropagation();
        setLoadingButton(transfer.id);
        try {
            const response = await TransferService.validateTransfer(transfer);
            toast.success('Transfert validé avec succès');
            onRefresh();
        } catch (error) {
            toast.error('Erreur lors de la validation du transfert');
            console.error('Erreur lors de la validation du transfert :', error);
        } finally {
            setLoadingButton(null);
        }
    };

    const formatDate = (dateString) => {
        return moment(dateString).format('DD MMMM YYYY');
    };

    const formatDateTime = (dateString) => {
        return moment(dateString).format('DD MMMM YYYY HH:mm');
    };

    const groupTransfersByDate = (transfers) => {
        // Trier les transferts par date de modification décroissante
        const sortedTransfers = [...transfers].sort((a, b) => {
            return new Date(b.modifiedAt) - new Date(a.modifiedAt);
        });

        return sortedTransfers.reduce((acc, transfer) => {
            const date = formatDate(transfer.modifiedAt);
            if (!acc[date]) {
                acc[date] = [];
            }
            acc[date].push(transfer);
            return acc;
        }, {});
    };

    const groupedTransfers = groupTransfersByDate(transfers);

    if (transfers.length === 0) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center py-5">
                <img
                    src="assets/images/setting/no_data.png"
                    alt="Aucun transfert"
                    className="mb-3"
                    style={{ width: '100px', opacity: 0.7 }}
                />
                <p className="text-muted">Aucun transfert rejeté n'est disponible</p>
            </div>
        );
    }

    return (
        <div className="transfer-list">
            {Object.keys(groupedTransfers).map((date) => (
                <div key={date}>
                    {/* Date Header */}
                    <div className="d-flex align-items-center my-4">
                        <hr className="flex-grow-1 border-top border-2 border-secondary opacity-25" />
                        <span className="px-3 py-1 bg-light  rounded-pill fw-bold" style={{ fontSize: '0.85rem' }}>
                            {date}
                        </span>
                        <hr className="flex-grow-1 border-top border-2 border-secondary opacity-25" />
                    </div>

                    {groupedTransfers[date].map((transfer, index) => (
                        <div
                            key={index}
                            className="card mb-3 shadow-sm border-danger border-1"
                            onClick={() => onTransferClick(transfer)}
                            style={{
                                cursor: 'pointer',
                                borderRadius: '12px',
                                overflow: 'hidden'
                            }}
                        >
                            <div className="card-body">
                                {/* Header with date and method */}
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <small className="text-muted">
                                        <FontAwesomeIcon icon={faClock} className="me-1" />
                                        {formatDateTime(transfer.modifiedAt)}
                                    </small>
                                    <span className="badge bg-light">
                                        <FontAwesomeIcon
                                            icon={getMethodIcon(transfer.withdrawalMethod)}
                                            className="me-1"
                                        />
                                        {transfer.withdrawalMethod}
                                    </span>
                                </div>

                                {/* Amount and status */}
                                <div
                                    className="d-flex justify-content-between align-items-center mb-3 p-3 bg-light rounded">
                                    <h4 className="mb-0 text-danger fw-bold">
                                        {transfer.transactionAmount} CAD
                                    </h4>


                                    <span className={`badge bg-danger text-white`}>
                                        {transfer.transactionStatus === 'REJECTED' ? 'Rejeté' : transfer.transactionStatus}
                                    </span>
                                </div>

                                {/* Transfer details */}
                                <div className="mb-3">

                                    <div className="d-flex align-items-start mb-2">
                                        <FontAwesomeIcon
                                            icon={faPaperPlane}
                                            className="me-2 mt-1 text-muted"
                                            style={{ width: '20px' }}
                                        />
                                        <div>
                                            <small className="text-muted d-block">Email de l'émetteur</small>
                                            <span>{transfer.issuerEmail}</span>
                                        </div>
                                    </div>

                                    <div className="d-flex align-items-start mb-2">
                                        <FontAwesomeIcon
                                            icon={faUser}
                                            className="me-2 mt-1 text-muted"
                                            style={{ width: '20px' }}
                                        />
                                        <div>
                                            <small className="text-muted d-block">Destinataire</small>
                                            <span>
                                                {transfer.recipientDetails.firstName} {transfer.recipientDetails.lastName}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="d-flex align-items-start mb-2">
                                        <FontAwesomeIcon
                                            icon={faEnvelope}
                                            className="me-2 mt-1 text-muted"
                                            style={{ width: '20px' }}
                                        />
                                        <div>
                                            <small className="text-muted d-block">Pays du destinataire</small>
                                            <span>{transfer.recipientDetails.country}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Reject reason */}
                                {transfer.rejectReason && (
                                    <div className="bg-danger bg-opacity-10 p-3 rounded mb-3 border-start border-danger border-4">
                                        <small className="text-danger d-block mb-1">Motif du rejet</small>
                                        <p className="mb-0">{transfer.rejectReason}</p>
                                    </div>
                                )}

                                {/* Reference */}
                                <div className="bg-light p-3 rounded mb-3">
                                    <small className="text-muted d-block mb-1">Référence</small>
                                    <code>{transfer.referenceNumber}</code>
                                </div>


                                {/*<RoleBasedRender roles={['ADMIN']}>
                                    <button
                                        className={`btn btn-success w-100 ${loadingButton === transfer.id ? 'disabled' : ''}`}
                                        onClick={(e) => handleValidateTransfer(e, transfer)}
                                        disabled={loadingButton === transfer.id}
                                    >
                                        {loadingButton === transfer.id ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Validation...
                                            </>
                                        ) : 'Valider le transfert'}
                                    </button>
                                </RoleBasedRender>*/}
                            </div>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};

export default RejectedTransferList;